const CONNECTION_TYPES = [
  'bluetooth',
  'cellular',
  'ethernet',
  'mixed',
  'none',
  'other',
  'unknown',
  'wifi',
  'wimax',
] as const;

const EFFECTIVE_TYPES = ['slow-2g', '2g', '3g', '4g'] as const;

export type NetworkConnectionType =
  | (typeof CONNECTION_TYPES)[number]
  | 'unsupported';

export type NetworkEffectiveType =
  | (typeof EFFECTIVE_TYPES)[number]
  | 'unknown'
  | 'unsupported';

export type NetworkInfo = {
  online: boolean;
  type: NetworkConnectionType;
  effectiveType: NetworkEffectiveType;
  downlinkMbps: number | null;
  rttMs: number | null;
  saveData: boolean | null;
  isSupported: boolean;
};

type BrowserNetworkInformation = EventTarget & {
  type?: string;
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
};

type NavigatorWithConnection = Navigator & {
  connection?: BrowserNetworkInformation;
};

const isConnectionType = (
  value: string
): value is (typeof CONNECTION_TYPES)[number] =>
  (CONNECTION_TYPES as readonly string[]).includes(value);

const isEffectiveType = (
  value: string
): value is (typeof EFFECTIVE_TYPES)[number] =>
  (EFFECTIVE_TYPES as readonly string[]).includes(value);

const getBrowserConnection = (): BrowserNetworkInformation | undefined =>
  (navigator as NavigatorWithConnection).connection;

/* 'unsupported' means the browser never exposed the field; 'unknown' means it
   reported a value outside the spec's list. Added this so we could
   tell a Safari user apart 
*/
const parseConnectionType = (
  value: string | undefined
): NetworkConnectionType => {
  if (value === undefined) return 'unsupported';
  return isConnectionType(value) ? value : 'unknown';
};

const parseEffectiveType = (
  value: string | undefined
): NetworkEffectiveType => {
  if (value === undefined) return 'unsupported';
  return isEffectiveType(value) ? value : 'unknown';
};

export const getNetworkInfo = (): NetworkInfo => {
  const connection = getBrowserConnection();
  const online = navigator.onLine;

  if (!connection) {
    return {
      online,
      type: 'unsupported',
      effectiveType: 'unsupported',
      downlinkMbps: null,
      rttMs: null,
      saveData: null,
      isSupported: false,
    };
  }

  return {
    online,
    type: parseConnectionType(connection.type),
    effectiveType: parseEffectiveType(connection.effectiveType),
    downlinkMbps:
      typeof connection.downlink === 'number' ? connection.downlink : null,
    rttMs: typeof connection.rtt === 'number' ? connection.rtt : null,
    saveData:
      typeof connection.saveData === 'boolean' ? connection.saveData : null,
    isSupported: true,
  };
};

//Calls `handler` whenever the connection or the online status changes.
export const subscribeToNetworkChange = (handler: () => void): (() => void) => {
  const connection = getBrowserConnection();

  connection?.addEventListener('change', handler);
  globalThis.addEventListener('online', handler);
  globalThis.addEventListener('offline', handler);

  return () => {
    connection?.removeEventListener('change', handler);
    globalThis.removeEventListener('online', handler);
    globalThis.removeEventListener('offline', handler);
  };
};
